import { PAL } from "./palette";
import { SCREEN_W } from "../shared/constants";

/**
 * Seasonal looks of the title screen: small pictures on both sides of the logo plate and an effect over the demo.
 * The admin picks the theme (or "auto": by the calendar, on the player's device). Pictures use the four game shades,
 * or real colours when the "Colors" option is on.
 */
export type Theme = "halloween" | "thanksgiving" | "movember" | "noel" | "stvalentin" | "stjean" | "fireworks";
export const THEME_NAMES: Theme[] = ["halloween", "thanksgiving", "movember", "noel", "stvalentin", "stjean", "fireworks"];

/** Second Monday of October (Canadian Thanksgiving) for a year. */
const thanksgivingDay = (y: number) => {
  const first = new Date(y, 9, 1).getDay(); // 0 = Sunday
  return 1 + ((8 - first) % 7) + 7;
};
/** The theme to show today: "off"/unknown -> none, "auto" -> by date, else the fixed theme. */
export function resolveTheme(setting: unknown, now = new Date()): Theme | null {
  if (typeof setting !== "string" || setting === "off") return null;
  if (setting !== "auto") return (THEME_NAMES as string[]).includes(setting) ? (setting as Theme) : null;
  const m = now.getMonth() + 1, d = now.getDate();
  const tg = thanksgivingDay(now.getFullYear());
  if (m === 10 && d >= 24) return "halloween";
  if (m === 10 && d >= tg - 7 && d <= tg) return "thanksgiving";
  if (m === 11) return "movember";
  if (m === 12 && d <= 26) return "noel";
  if (m === 2 && d >= 7 && d <= 14) return "stvalentin";
  if (m === 6 && d >= 17 && d <= 24) return "stjean";
  if (m === 7 && d <= 4) return "fireworks";
  return null;
}

type Ink = string; // one letter of a picture
/** Colour of each letter, and the game shade used when colours are off. */
const INKS: Record<string, [string, number]> = {
  k: ["#1c2830", 3], // outline
  a: ["#f08a1c", 2], // orange
  g: ["#3a9a3a", 2], // green
  r: ["#d63a30", 2], // red
  w: ["#ffffff", 0], // white
  y: ["#f6d84a", 1], // yellow
  b: ["#1f4fa8", 2], // blue
  p: ["#f0587a", 1], // pink
  n: ["#8a5a30", 2], // brown
  d: ["#5a3a1c", 3], // dark brown
  h: ["#c08a4e", 1], // light brown
  o: ["#c8580c", 2], // dark orange (pumpkin ribs)
  v: ["#3a2a50", 3], // bat purple
};
function blit(ctx: CanvasRenderingContext2D, rows: string[], x: number, y: number, colour: boolean) {
  rows.forEach((row, j) => {
    for (let i = 0; i < row.length; i++) {
      const ch: Ink = row[i];
      const ink = INKS[ch];
      if (!ink) continue;
      ctx.fillStyle = colour ? ink[0] : PAL[ink[1]];
      ctx.fillRect(x + i, y + j, 1, 1);
    }
  });
}
/** Leans a picture over: the top moves right by `k` pixels per row. */
const lean = (rows: string[], k: number) => {
  const w = rows[0].length, h = rows.length, pad = Math.ceil(h * k) + 1;
  return rows.map((r, j) => {
    const s = Math.round((h - 1 - j) * k);
    return ".".repeat(pad - s) + r + ".".repeat(s) + ""; // keeps every row the same width
  }).map((r) => r.slice(0, w + pad));
};

/** Jack-o'-lantern, 16 x 14: ribbed orange body, green stem, a glowing carved face. */
function pumpkin(): string[] {
  const W = 16, H = 14;
  const rows: string[][] = Array.from({ length: H }, () => Array(W).fill("."));
  for (let j = 2; j < H; j++)
    for (let i = 0; i < W; i++) {
      const d = Math.hypot((i - 7.5) / 7.7, (j - 8) / 6.1);
      if (d <= 1) rows[j][i] = d > 0.84 ? "k" : Math.abs(i - 7.5) > 6.8 ? "o" : "a";
    }
  for (let j = 3; j < H - 1; j++) for (const i of [3, 12, 7, 8]) if (rows[j][i] === "a" && (i === 3 || i === 12)) rows[j][i] = "o";
  for (const [i, j] of [[7, 0], [8, 0], [7, 1], [8, 1], [9, 1]]) rows[j][i] = "g";
  for (const [i, j] of [[6, 0], [9, 0], [6, 1], [10, 1], [7, 2], [8, 2]]) rows[j][i] = "k";
  // carved face
  for (const [i, j] of [[4, 6], [3, 7], [4, 7], [5, 7], [11, 6], [10, 7], [11, 7], [12, 7], [7, 8], [8, 8]]) rows[j][i] = "y";
  for (const [i, j] of [[3, 10], [4, 10], [5, 10], [6, 10], [7, 10], [8, 10], [9, 10], [10, 10], [11, 10], [12, 10], [4, 11], [5, 11], [7, 11], [8, 11], [10, 11], [11, 11]]) rows[j][i] = "y";
  rows[10][5] = "k";
  rows[10][10] = "k";
  rows[11][6] = "k";
  rows[11][9] = "k";
  return rows.map((r) => r.join(""));
}
const BAT = [
  ["k...k.k...k", "kk.kkkkk.kk", "kkkkkkkkkkk", ".kkk.k.kkk.", "..k.....k.."],
  ["....k.k....", ".kk.kkk.kk.", "kkkkkkkkkkk", "k.kkk.kkk.k", "...k...k..."],
];
const HEART = [
  ".kk...kk.",
  "kwpk.kppk",
  "kpppkpppk",
  "kpppppppk",
  ".kpppppk.",
  "..kpppk..",
  "...kpk...",
  "....k....",
];
const MOUSTACHE = [
  "................",
  "k.....kkkk.....k",
  "kk..kkhhhhkk..kk",
  "knkkhhnnnnhhkknk",
  ".knnnnnddnnnnnk.",
  "..knnddkkddnnk..",
  "...kkkk..kkkk...",
];
const MINI_MOUSTACHE = ["knnk.knnk", "kdnnknnnk", ".kkk.kkk."];
/** Flag of Quebec, 16 x 11: a white cross on blue with a fleur-de-lis in each quarter. */
function flag(): string[] {
  const rows: string[][] = Array.from({ length: 11 }, () => Array(16).fill("b"));
  for (let j = 0; j < 11; j++) for (let i = 0; i < 16; i++) if (i === 7 || i === 8 || j === 4 || j === 5) rows[j][i] = "w";
  for (const [cx, cy] of [[3, 2], [11, 2], [3, 8], [11, 8]]) for (const [dx, dy] of [[0, -1], [-1, 0], [0, 0], [1, 0], [0, 1]]) rows[cy + dy][cx + dx] = "w";
  const edge = (j: number, i: number) => (j === 0 || j === 10 || i === 0 || i === 15 ? "k" : rows[j][i]);
  return rows.map((r, j) => r.map((_, i) => edge(j, i)).join(""));
}
/** Christmas wreath, 15 x 15: a green ring, a red bow at the bottom, a few berries. */
function wreath(): string[] {
  const rows: string[][] = Array.from({ length: 15 }, () => Array(15).fill("."));
  for (let j = 0; j < 15; j++)
    for (let i = 0; i < 15; i++) {
      const d = Math.hypot(i - 7, j - 7);
      if (d <= 7.2 && d >= 3.6) rows[j][i] = d > 6.4 || d < 4.4 ? "k" : "g";
    }
  for (const [i, j] of [[3, 4], [11, 4], [2, 9], [12, 9], [7, 1]]) rows[j][i] = "r";
  for (const [i, j] of [[5, 12], [6, 13], [8, 13], [9, 12], [7, 13], [6, 14], [8, 14]]) rows[j][i] = i === 7 ? "y" : "r";
  return rows.map((r) => r.join(""));
}
/** Turkey, 15 x 14: a fan of tail feathers (outlined), a plump brown body, a head with a red wattle. */
function turkey(): string[] {
  const W = 15, H = 14;
  const rows: string[][] = Array.from({ length: H }, () => Array(W).fill("."));
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) {
      const d = Math.hypot(i - 6, (j - 9) * 1.1);
      if (j <= 9 && d <= 7) rows[j][i] = d > 6 ? "k" : d > 4.6 ? "r" : d > 3.2 ? "a" : "y";
    }
  for (let j = 6; j < 13; j++) for (let i = 2; i < 11; i++) {
    const d = Math.hypot((i - 6) * 0.9, j - 9.5);
    if (d <= 3.6) rows[j][i] = d > 2.7 ? "k" : "n";
  }
  for (const [i, j] of [[9, 5], [10, 5], [11, 5], [9, 6], [10, 6], [11, 6], [10, 7]]) rows[j][i] = "n";
  for (const [i, j] of [[8, 4], [9, 4], [10, 4], [11, 4], [12, 5], [12, 6], [8, 5], [8, 6], [9, 7], [11, 7], [10, 8], [9, 8]]) if (rows[j][i] !== "n") rows[j][i] = "k";
  rows[5][10] = "w";
  rows[6][11] = "k";
  rows[6][12] = "y";
  rows[7][10] = "r";
  rows[8][10] = "r";
  rows[13][4] = "k";
  rows[13][8] = "k";
  rows[12][4] = "y";
  rows[12][8] = "y";
  return rows.map((r) => r.join(""));
}
const PUMPKIN = pumpkin();
const FLAG = flag(), WREATH = wreath(), TURKEY = turkey();
const LEFT_X = 1, RIGHT_X = SCREEN_W - 1; // the logo plate spans x 18..142: pictures live in the 18 px on each side

/** Pictures next to the logo plate (y 2..17). */
export function drawThemeLogo(ctx: CanvasRenderingContext2D, theme: Theme, colour: boolean) {
  switch (theme) {
    case "halloween": {
      const p = lean(PUMPKIN, 0.18);
      blit(ctx, p, RIGHT_X - p[0].length, 3, colour);
      blit(ctx, BAT[0], LEFT_X + 3, 6, colour);
      break;
    }
    case "noel":
      blit(ctx, WREATH, RIGHT_X - 15 - 1, 2, colour);
      break;
    case "stvalentin":
      blit(ctx, HEART, LEFT_X + 4, 6, colour);
      blit(ctx, HEART, RIGHT_X - 13, 6, colour);
      break;
    case "stjean":
      blit(ctx, FLAG, LEFT_X, 4, colour);
      blit(ctx, FLAG, RIGHT_X - 16, 4, colour);
      break;
    case "thanksgiving":
      blit(ctx, TURKEY, LEFT_X, 2, colour);
      blit(ctx, TURKEY.map((r) => [...r].reverse().join("")), RIGHT_X - 15, 2, colour);
      break;
    case "movember":
      blit(ctx, MOUSTACHE, LEFT_X + 1, 6, colour);
      blit(ctx, MOUSTACHE, RIGHT_X - 17, 6, colour);
      break;
    default:
      break;
  }
}

// ---- effects over the demo (y 0..78): drawn before the logo plate and the menu, which cover them ----
const hash = (n: number) => {
  let h = Math.imul(n + 1, 2654435761) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  return (h ^ (h >>> 13)) >>> 0;
};
const unit = (n: number) => (hash(n) % 10000) / 10000;
const H = 78;
const px = (ctx: CanvasRenderingContext2D, x: number, y: number, c: string, s = 1) => {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), s, s);
};
const BURST = [["#ff5a4a", 2], ["#ffd84a", 1], ["#5ad2ff", 1], ["#9cf05a", 1], ["#ff7ad0", 2]] as const;

export function drawThemeEffect(ctx: CanvasRenderingContext2D, theme: Theme, frame: number, colour: boolean) {
  if (theme === "noel") {
    for (let i = 0; i < 46; i++) {
      const speed = 0.25 + unit(i * 3) * 0.55;
      const y = (frame * speed + unit(i * 5) * H) % H;
      const x = (unit(i * 7) * SCREEN_W + Math.sin(frame * 0.03 + i) * 3 + SCREEN_W) % SCREEN_W;
      px(ctx, x, y, colour ? "#ffffff" : PAL[0], speed > 0.6 ? 2 : 1);
    }
  } else if (theme === "stvalentin") {
    for (let i = 0; i < 9; i++) {
      const y = H - ((frame * (0.2 + unit(i * 3) * 0.3) + unit(i * 5) * H) % (H + 8)) + 4;
      const x = (unit(i * 7) * (SCREEN_W - 10) + Math.sin(frame * 0.04 + i * 2) * 4) | 0;
      const c = colour ? (i % 2 ? "#ff6a8a" : "#ff3a5a") : PAL[i % 2 ? 1 : 0];
      ctx.fillStyle = c;
      for (const [dx, dy, w] of [[1, 0, 2], [4, 0, 2], [0, 1, 7], [0, 2, 7], [1, 3, 5], [2, 4, 3], [3, 5, 1]]) ctx.fillRect(x + dx, Math.round(y) + dy, w, 1);
    }
  } else if (theme === "thanksgiving") {
    for (let i = 0; i < 16; i++) {
      const speed = 0.3 + unit(i * 3) * 0.4;
      const y = (frame * speed + unit(i * 5) * H) % H;
      const x = (unit(i * 7) * SCREEN_W + Math.sin(frame * 0.05 + i) * 7 + SCREEN_W) % SCREEN_W;
      const c = colour ? ["#e0701c", "#c8402a", "#e8a020", "#8a5a30"][i % 4] : PAL[i % 2 ? 1 : 2];
      px(ctx, x, y, c, 2);
      px(ctx, x + 1, y + 1, c, 1);
    }
  } else if (theme === "halloween") {
    for (let i = 0; i < 5; i++) {
      const speed = 0.35 + unit(i * 3) * 0.45;
      const span = SCREEN_W + 24;
      const dir = i % 2 ? -1 : 1;
      const t = (frame * speed + unit(i * 5) * span) % span;
      const x = dir > 0 ? t - 12 : SCREEN_W + 12 - t;
      const y = 8 + unit(i * 7) * 50 + Math.sin(frame * 0.07 + i * 2) * 5;
      blit(ctx, BAT[Math.floor(frame / 6 + i) % 2].map((r) => r), Math.round(x), Math.round(y), colour);
    }
  } else if (theme === "movember") {
    for (let i = 0; i < 7; i++) {
      const speed = 0.2 + unit(i * 3) * 0.3;
      const y = (frame * speed + unit(i * 5) * (H + 6)) % (H + 6) - 4;
      const x = (unit(i * 7) * (SCREEN_W - 12) + Math.sin(frame * 0.035 + i * 2) * 6) | 0;
      blit(ctx, MINI_MOUSTACHE, x, Math.round(y), colour);
    }
  } else if (theme === "fireworks") {
    for (let k = 0; k < 3; k++) {
      const cyc = 96, f = (frame + k * 32) % cyc, n = Math.floor((frame + k * 32) / cyc) * 3 + k;
      const cx = 24 + unit(n * 11) * 112, top = 26 + unit(n * 13) * 22;
      const [colr, tone] = BURST[Math.floor(unit(n * 17) * BURST.length)];
      const c = colour ? colr : PAL[tone === 2 ? 1 : 0];
      if (f < 22) {
        const y = H - (H - top) * (f / 22);
        px(ctx, cx, y, colour ? "#ffe9a0" : PAL[0]);
        px(ctx, cx, y + 2, colour ? "#c88a30" : PAL[1]);
      } else if (f < 80) {
        const t = (f - 22) / 58, rad = 24 * Math.sqrt(t) + 2;
        const dim = t > 0.65;
        for (let a = 0; a < 26; a++) {
          const ang = (a / 26) * Math.PI * 2 + unit(n) * 2;
          px(ctx, cx + Math.cos(ang) * rad, top + Math.sin(ang) * rad + t * t * 14, dim && a % 2 ? (colour ? "#b0b0b0" : PAL[1]) : c, 2);
        }
      }
    }
  }
}
void ({} as Ink);
